import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const DataCleansingPanel = ({ 
  leads = [], 
  onCleanseData, 
  isProcessing, 
  selectedLeads = [] 
}) => {
  const [cleansingOptions, setCleansingOptions] = useState({
    duplicateDetection: true,
    dataStandardization: true,
    missingDataCompletion: true,
    emailNormalization: true,
    phoneFormatting: true,
    addressStandardization: false,
    nameFormatting: true,
    companyNameNormalization: true
  });

  const [cleansingResults, setCleansingResults] = useState(null);

  const handleOptionToggle = (option) => {
    setCleansingOptions(prev => ({
      ...prev,
      [option]: !prev?.[option]
    }));
  };

  const handleStartCleansing = () => {
    if (selectedLeads?.length > 0) {
      onCleanseData?.(selectedLeads, cleansingOptions);
      
      // Mock cleansing results
      setTimeout(() => {
        setCleansingResults({
          processed: selectedLeads?.length,
          duplicatesFound: Math.floor(selectedLeads?.length * 0.15),
          duplicatesRemoved: Math.floor(selectedLeads?.length * 0.12),
          dataStandardized: Math.floor(selectedLeads?.length * 0.85),
          missingDataCompleted: Math.floor(selectedLeads?.length * 0.62),
          emailsNormalized: Math.floor(selectedLeads?.length * 0.78),
          phonesFormatted: Math.floor(selectedLeads?.length * 0.71),
          namesFormatted: Math.floor(selectedLeads?.length * 0.45),
          companiesNormalized: Math.floor(selectedLeads?.length * 0.67)
        });
      }, 2000);
    }
  };

  const getDuplicateCount = () => {
    const duplicateLeads = leads?.filter(lead => 
      lead?.duplicateStatus === 'potential_duplicate'
    ) || [];
    return duplicateLeads?.length;
  };

  const getDataIssuesCount = () => {
    const issueLeads = leads?.filter(lead => 
      !lead?.emailVerified || 
      !lead?.phoneVerified || 
      lead?.profileComplete < 70
    ) || [];
    return issueLeads?.length;
  };

  const cleansingStats = [
    {
      label: 'Potential Duplicates',
      value: getDuplicateCount(),
      icon: 'Copy',
      color: 'text-yellow-600'
    },
    {
      label: 'Data Issues',
      value: getDataIssuesCount(),
      icon: 'AlertTriangle',
      color: 'text-red-600'
    },
    {
      label: 'Selected for Cleansing',
      value: selectedLeads?.length,
      icon: 'CheckSquare',
      color: 'text-blue-600'
    },
    {
      label: 'Quality Score Improvement',
      value: '18%',
      icon: 'TrendingUp',
      color: 'text-green-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Cleansing Stats */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          Lead Cleansing Operations
        </h2>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          {cleansingStats?.map((stat, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-muted ${stat?.color}`}>
                <Icon name={stat?.icon} size={20} />
              </div>
              <div>
                <div className="text-lg font-semibold text-foreground">{stat?.value}</div>
                <div className="text-sm text-muted-foreground">{stat?.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cleansing Options */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          AI Inference Algorithms
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {Object?.entries(cleansingOptions)?.map(([option, enabled]) => (
            <div key={option} className="flex items-center space-x-3">
              <Checkbox
                id={option}
                checked={enabled}
                onChange={() => handleOptionToggle(option)}
              />
              <div>
                <label 
                  htmlFor={option} 
                  className="text-sm font-medium text-foreground cursor-pointer"
                >
                  {option?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                </label>
                <div className="text-xs text-muted-foreground">
                  {getOptionDescription(option)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {selectedLeads?.length} leads selected for cleansing
          </div>
          <Button
            onClick={handleStartCleansing}
            disabled={selectedLeads?.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Icon name="Sparkles" size={16} className="mr-2" />
                Start Cleansing
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Cleansing Results */}
      {cleansingResults && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Cleansing Results
            </h3>
            <Icon name="CheckCircle" size={20} className="text-green-600" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-foreground">{cleansingResults?.processed}</div>
              <div className="text-sm text-muted-foreground">Processed</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{cleansingResults?.duplicatesRemoved}</div>
              <div className="text-sm text-muted-foreground">Duplicates Removed</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{cleansingResults?.dataStandardized}</div>
              <div className="text-sm text-muted-foreground">Data Standardized</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-green-600">{cleansingResults?.missingDataCompleted}</div>
              <div className="text-sm text-muted-foreground">Missing Data Completed</div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Emails Normalized:</span>
              <span className="text-foreground font-medium">{cleansingResults?.emailsNormalized} records</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Phones Formatted:</span>
              <span className="text-foreground font-medium">{cleansingResults?.phonesFormatted} records</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Names Formatted:</span>
              <span className="text-foreground font-medium">{cleansingResults?.namesFormatted} records</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Companies Normalized:</span>
              <span className="text-foreground font-medium">{cleansingResults?.companiesNormalized} records</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Overall Quality Improvement:</span>
              <span className="text-lg font-semibold text-green-600">+18.5%</span>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Detection Results */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Duplicate Detection
        </h3>
        
        <div className="space-y-3">
          {leads?.filter(lead => lead?.duplicateStatus === 'potential_duplicate')?.slice(0, 3)?.map((lead) => (
            <div key={lead?.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-foreground">{lead?.name}</div>
                  <div className="text-sm text-muted-foreground">{lead?.email} • {lead?.company}</div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">
                    <Icon name="Eye" size={14} />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Icon name="Trash2" size={14} className="text-red-600" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          
          {getDuplicateCount() === 0 && (
            <div className="text-center py-4 text-muted-foreground">
              No duplicate leads detected
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getOptionDescription = (option) => {
  const descriptions = {
    duplicateDetection: 'Identify and merge duplicate contact records',
    dataStandardization: 'Normalize data formats and structures',
    missingDataCompletion: 'Fill missing information using AI inference',
    emailNormalization: 'Standardize email formats and domains',
    phoneFormatting: 'Format phone numbers consistently',
    addressStandardization: 'Normalize address formats and geocode',
    nameFormatting: 'Standardize name formats and capitalization',
    companyNameNormalization: 'Normalize company names and remove duplicates'
  };
  return descriptions?.[option] || '';
};

export default DataCleansingPanel;